import React, { ChangeEvent, useEffect, useState } from 'react'
import Link from 'next/link';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { IException } from '@/base/models';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { encryptData } from '@/base/utils/encryption';
import { AccountNameAPI, InstanceAPI } from '@/api/instance';
import LoadingSpinner from '@/components/loading-spinner/loadingSpinner';
import StoneityButton from '@/components/stoneity-button/stoneityButton'
import TextField from '@/components/text-field/textField';
import ButtonArrow from '../images/buttonArrow';
import Swal from 'sweetalert2';
import styles from '../../styles/TryDemo.module.css'
import Image, { ImageLoaderProps } from 'next/image';
import { IMAGE_DELIVERY, imageLoader } from '@/base/utils/dataHelper';
import StoneityLogo from '../images/stoneityLogo';
import InfoApprove from '../images/infoApprove';

interface InstanceData {
  instanceName: string,
  email: string,
  firstName: string,
  lastName: string,
  phone: string,
  purpose: Purpose,
  role: Role,
  team: Team,
  sector: Sector
}

enum Purpose {
  job = 1,
  personal,
  school,
  nonProfit
}

enum Role {
  underGraduade = 1,
  graduate,
  facultyMember,
  other
}

enum Team {
  singlePerson = 1,
  "2-5",
  '6-10',
  '11-15',
  '25-50',
  '51-100',
  '101-500'
}

enum Sector {
  finance = 1,
  education,
  design,
  marketing,
  construction,
  operations,
  productManagement,
  law,
  softwareDevelopment,
  salesAndCRM,
  humanResources
}

const TryDemo = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [userPageChoseTab, setUserPageChoseTab] = useState(false);
  const [isFirstStep, setIsFirstStep] = useState(true);
  const [isSecondStep, setIsSecondStep] = useState(false);
  const [isThirdStep, setIsThirdStep] = useState(false);
  const [isFourthStep, setIsFourthStep] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreated, setIsCreated] = useState(false);

  const initialInstanceData: InstanceData = {
    email: "",
    firstName: "",
    instanceName: "",
    lastName: "",
    phone: "",
    purpose: Purpose.job,
    role: Role.underGraduade,
    sector: Sector.finance,
    team: Team.singlePerson
  }

  const [instanceData, setInstanceData] = useState<InstanceData>(initialInstanceData)
  const [inputErrors, setInputErrors] = useState({ email: "", firstName: "", instanceName: "", lastName: "", phone: "" })
  // Canli hesap adi kontrolu: yazarken (debounce) sunucuya sorulur; input icinde spinner,
  // uygunsa yesil / degilse kirmizi cerceve. Nihai karar her zaman sunucudadir.
  const [nameCheck, setNameCheck] = useState<'idle' | 'checking' | 'ok' | 'taken'>('idle');

  useEffect(() => {
    const name = instanceData.instanceName;
    if (!isValidInstanceName(name)) {
      setNameCheck('idle');
      return;
    }
    setNameCheck('checking');
    const handle = setTimeout(async () => {
      try {
        const res = await new AccountNameAPI({}, name).check();
        // Gecikmis yanit: kullanici bu arada adi degistirdiyse sonucu yok say.
        setInstanceData((current) => {
          if (current.instanceName === name) {
            setNameCheck(res?.Available ? 'ok' : 'taken');
          }
          return current;
        });
      } catch {
        // Ag hatasi kullaniciyi engellemesin; sunucu submit'te zaten reddeder.
        setNameCheck('idle');
      }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceData.instanceName]);

  // Conversion event: Info ekranı görüntülendiğinde (demoyu başarıyla oluşturduğunda)
  useEffect(() => {
    if (isCreated && typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'conversion', {
        send_to: 'AW-17223804847/h6jDCLK-0N0aEK_P-ZRA',
        value: 1.0,
        currency: 'TRY'
      });
    }
  }, [isCreated]);

  const closeSteps = () => {
    setIsFirstStep(false),
      setIsSecondStep(false),
      setIsThirdStep(false),
      setIsFourthStep(false)
  }

  const isValidInstanceName = (value: string): boolean => {
    // Sunucudaki SubdomainPolicyService.FormatRegex ile ayni kural: 3-30, a-z 0-9,
    // tek tek tire; bas/son tire ve ard arda tire yok. Nihai karar her zaman sunucuda.
    const regex = /^(?!.*--)[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;
    return regex.test(value);
  };

  const isValidInputs = (fieldValues = instanceData): boolean | undefined => {
    const temp = inputErrors;

    const firstNameRegex = new RegExp(`^[A-Za-z0-9ğüşöçıİĞÜŞÖÇ]+([ ]?[A-Za-z0-9ğüşöçıİĞÜŞÖÇ])*$`, 'gm',);
    const lastNameRegex = new RegExp(`^[A-Za-z0-9ğüşöçıİĞÜŞÖÇ]{2,100}$`, 'gm',);
    const emailValidation = new RegExp(/(^[a-zA-Z0-9][\w\.\-_]+)\w+@[a-zA-Z0-9][a-zA-Z0-9\-]+(\.[a-zA-Z0-9]+){1,}$/, 'gim',);
    // const phoneValidation = new RegExp(`^[\+]?([0-9 ]){4,15}[0-9]$`, 'gm',);

    if ('instanceName' in fieldValues) {
      temp.instanceName = isValidInstanceName(instanceData.instanceName) ? "" : t("invalidAccountName");
    }

    if ('firstName' in fieldValues) {
      temp.firstName = fieldValues.firstName &&
        fieldValues.firstName.match(firstNameRegex) === null ? t("pleaseInputFirstName") :
        fieldValues.firstName.trim().length < 2 ? t("userFirstNameMinError") :
          fieldValues.firstName.trim().length > 100 ? t("userFirstNameMaxError") : ""
    }

    if ('lastName' in fieldValues) {
      temp.lastName = fieldValues.lastName && fieldValues.lastName.trim().length < 2 ? t("userLastNameMinError") :
        fieldValues.lastName.trim().length > 100 ? t("userLastNameMaxError") :
          fieldValues.lastName.match(lastNameRegex) !== null ? "" : t("pleaseInputSurname");
    }

    if ('email' in fieldValues && temp.email != t("pleaseInputEmail")) {
      temp.email = fieldValues.email.match(emailValidation) ? "" : t("pleaseInputCorrectEmailFormat")
    }

    // if ('phone' in fieldValues) {
    //   temp.phone =
    //     fieldValues.phone && checkIsInputTrimmed(fieldValues.phone) ?
    //       fieldValues.phone.replace('+', '').trim().length > 16 ?
    //         t('phoneNumberMax')
    //         : fieldValues.phone.replace('+', '').trim().length < 5 ?
    //           t('phoneNumberMin')
    //           : fieldValues.phone.match(phoneValidation) !== null ?
    //             "" : t('phoneIsNotValidate')
    //       : t('pleaseInputPhone')
    // }

    setInputErrors({ ...temp })

    if (fieldValues === instanceData) {
      return Object.values(temp).every(x => x === "")
    }
  }


  // Buton kapisi: zorunlu alanlar gecerli VE hesap adi sunucuca 'kullanilabilir'
  // olmadan Create Demo Instance tiklanamaz. isValidInputs'tan farki: hata mesaji
  // yazmadan sessizce hesaplanir (her tusta error state'i kirletmemek icin).
  const emailRegexSilent = /(^[a-zA-Z0-9][\w\.\-_]+)\w+@[a-zA-Z0-9][a-zA-Z0-9\-]+(\.[a-zA-Z0-9]+){1,}$/;
  const isFormReady =
    nameCheck === 'ok' &&
    isValidInstanceName(instanceData.instanceName) &&
    emailRegexSilent.test(instanceData.email.trim()) &&
    instanceData.firstName.trim().length >= 2 &&
    instanceData.lastName.trim().length >= 2;

  const createDemoInstance = async () => {
    if (!isValidInputs()) {
      return
    }

    if (nameCheck === 'taken') {
      setInputErrors((prev) => ({ ...prev, instanceName: t('accountNameUnavailable') }));
      return
    }

    setIsLoading(true);

    try {
      const timeStamp = new Date().toISOString();
      const encryptedData = encryptData(instanceData, timeStamp);

      const instanceApi = new InstanceAPI({});
      const res = await instanceApi.createInstance({ instanceData: encryptedData, requestedDate: timeStamp });

      setIsLoading(false);

      if (res.Created && res.InstanceUrl) {
        setIsCreated(true);
      } else {
        Swal.fire({
          icon: "warning",
          text: t('unexpectedError'),
          allowOutsideClick: false,
        });
      }
    } catch (error) {
      setIsLoading(false);

      const err: IException = error as IException;

      Swal.fire({
        icon: "warning",
        text: err?.description || t('unexpectedError'),
        allowOutsideClick: false,
      });
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setInstanceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const Info = () => {
    return (
      <div className={styles.infoPage}>
        <div className={styles.infoContent}>
          <InfoApprove />
          <div>
            <div className={styles.infoStoneityLogo}>
              <StoneityLogo />
            </div>
            <div className={styles.infoStoneityLogo}>
              <StoneityLogo />
            </div>
            <div className={styles.infoStoneityLogo}>
              <StoneityLogo />
            </div>
            <div className={styles.infoStoneityLogo}>
              <StoneityLogo />
            </div>
          </div>
          <div className={styles.infoContentRight}>
            {t("demoComplatedText")}
            <span className={styles.infoContentInfoText}>
              {t("pleaseCheckYourEmail")} <strong>{instanceData.email}</strong>
            </span>
            <Link href={"/"} style={{ textDecoration: 'none', width: "max-content" }}>
              <StoneityButton
                buttonStyle='dark'
                buttonText={t('goToHomepage')}
                width={200}
                height={45}
              />
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = `/try-demo`;

  return (
    <>
      <Head>
        <title>{t('seo_try_demo_title')}</title>
        <meta name="description" content={t('seo_try_demo_description')} />
        <meta name="keywords" content="ücretsiz deneme, kredi kartı gerekmez, Stoneity deneme, free trial, no credit card, try Stoneity" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_try_demo_title')} />
        <meta property="og:description" content={t('seo_try_demo_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_try_demo_title')} />
        <meta name="twitter:description" content={t('seo_try_demo_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <div className={styles.userPageWrapper}>
        {/* {isLoading && <LoadingSpinner />} */}
        {isLoading &&
          <div className={styles.spinner}>
            <div className={styles.spinnerContent}>
              <div className={styles.logo}>
                <StoneityLogo width={"80"} height={"80"} />
              </div>
              <div className={styles.homeBannerAnimationTextWrapper}>
                <ul className={styles.homeBannerAnimationTextContent}>
                  <li className={styles.homeBannerAnimationTextItem}>{t("creatingAccount")}...</li>
                  <li className={styles.homeBannerAnimationTextItem}>{t("pleaseWait")}...</li>
                  <li className={styles.homeBannerAnimationTextItem}>{t("preparingSetup")}...</li>
                </ul>
              </div>
            </div>
          </div>
        }
        {isCreated &&
          <Info />
        }
        <div className={styles.userPageImgWrapper}>
          {isFirstStep &&
            <Image
              className={styles.firstImg}
              src={`${IMAGE_DELIVERY}/46b6b246-a743-4df1-c0fc-46aa6960bf00`}
              alt=""
              width={680}
              height={420}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 680, 420)}
            />
          }
          {isSecondStep &&
            <Image
              className={styles.secondImg}
              src={`${IMAGE_DELIVERY}/dd3f306c-ef5c-4ec6-7145-4cc0827bb500`}
              alt=""
              width={570}
              height={430}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 570, 430)}
            />
          }
          {isThirdStep &&
            <Image
              className={styles.thirdImg}
              src={`${IMAGE_DELIVERY}/60303261-804c-4086-2172-fd3eb892cf00`}
              alt=""
              width={695}
              height={530}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 695, 530)}
            />
          }
          {isFourthStep &&
            <Image
              className={styles.thirdImg}
              src={`${IMAGE_DELIVERY}/60303261-804c-4086-2172-fd3eb892cf00`}
              alt=""
              width={695}
              height={530}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 695, 530)}
            />
          }
        </div>
        <div className={styles.userPageContentWrapper}>
          {isFirstStep &&
            <div className={styles.userPageContent}>
              <Link href={"/"}>
                <Image
                  src={`${IMAGE_DELIVERY}/a7ef1d5a-a230-4954-ccc7-add056564300`}
                  alt=""
                  width={150}
                  height={37}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 150, 37)}
                />
              </Link>
              <div className={styles.userPageChoseWrapper}>
                <div className={styles.userPageChoseContent}>
                  <div className={styles.title}>{t('whatBringsYouHere')}</div>
                  <div className={styles.userPageChoseTabWrapper}>
                    {Object.values(Purpose)
                      .filter(x => typeof x === "string")
                      .map((purpose, index) => (
                        <div
                          key={index}
                          className={`${styles.userPageChoseTab} ${instanceData.purpose === Purpose[purpose] ? styles.userPageChoseTabActive : ''}`}
                          onClick={() => setInstanceData({ ...instanceData, purpose: Purpose[purpose] })}
                        >
                          {t(purpose?.toString())}
                        </div>
                      ))}
                  </div>
                </div>
                <div className={styles.userPageChoseContent}>
                  <div className={styles.title}>{t('howDoYouDescribeYourCurrentRole')}</div>
                  <div className={styles.userPageChoseTabWrapper}>
                    {Object.values(Role)
                      .filter(x => typeof x === 'string')
                      .map((role, index) => (
                        <div
                          key={index}
                          className={`${styles.userPageChoseTab} ${instanceData.role === Role[role] ? styles.userPageChoseTabActive : ''}`}
                          onClick={() => setInstanceData({ ...instanceData, role: Role[role] })}
                        >
                          {t(role?.toString())}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <StoneityButton
                buttonStyle='dark'
                buttonText={t('continue')}
                iconPosition='end'
                iconStyle={{ width: '16px', height: '19px' }}
                icon={<ButtonArrow />}
                width={150}
                height={45}
                onClick={() => { closeSteps(), setIsSecondStep(true) }}
              />
            </div>
          }
          {isSecondStep &&
            <div className={styles.userPageContent}>
              <Link href={"/"}>
                <Image
                  src={`${IMAGE_DELIVERY}/a7ef1d5a-a230-4954-ccc7-add056564300`}
                  alt=""
                  width={150}
                  height={37}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 150, 37)}
                />
              </Link>
              <div className={styles.userPageChoseWrapper}>
                <div className={styles.userPageChoseContent}>
                  <div className={styles.title}>{t('howManyPersonDoYouHaveInYourTeam')}</div>
                  <div className={styles.userPageChoseTabWrapper}>
                    {Object.values(Team)
                      .filter(x => typeof x === 'string')
                      .map((team, index) => (
                        <div
                          key={index}
                          className={`${styles.userPageChoseTab} ${instanceData.team === Team[team] ? styles.userPageChoseTabActive : ''}`}
                          onClick={() => setInstanceData({ ...instanceData, team: Team[team] })}
                        >
                          {t(team?.toString())}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div className={styles.buttonWrapper}>
                <StoneityButton
                  buttonStyle='light'
                  buttonText={t('back')}
                  iconPosition='start'
                  iconStyle={{ width: '16px', height: '19px' }}
                  icon={<ButtonArrow rotate={'180deg'} fill={'#00323D'} />}
                  width={95}
                  height={45}
                  onClick={() => { closeSteps(), setIsFirstStep(true) }}
                />
                <StoneityButton
                  buttonStyle='dark'
                  buttonText={t('continue')}
                  iconPosition='end'
                  iconStyle={{ width: '16px', height: '19px' }}
                  icon={<ButtonArrow />}
                  width={150}
                  height={45}
                  onClick={() => { closeSteps(), setIsThirdStep(true) }}
                />
              </div>
            </div>
          }
          {isThirdStep &&
            <div className={styles.userPageContent}>
              <Link href={"/"}>
                <Image
                  src={`${IMAGE_DELIVERY}/a7ef1d5a-a230-4954-ccc7-add056564300`}
                  alt=""
                  width={150}
                  height={37}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 150, 37)}
                />
              </Link>
              <div className={styles.userPageChoseWrapper}>
                <div className={styles.userPageChoseContent}>
                  <div className={styles.title}>{t('whichSectorAreYouFrom')}</div>
                  <div className={styles.userPageChoseTabWrapper}>
                    {Object.values(Sector)
                      .filter(x => typeof x === 'string')
                      .map((sector, index) => (
                        <div
                          key={index}
                          className={`${styles.userPageChoseTab} ${instanceData.sector === Sector[sector] ? styles.userPageChoseTabActive : ''}`}
                          onClick={() => setInstanceData({ ...instanceData, sector: Sector[sector] })}
                        >
                          {t(sector?.toString())}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div className={styles.buttonWrapper}>
                <StoneityButton
                  buttonStyle='light'
                  buttonText={t('back')}
                  iconPosition='start'
                  iconStyle={{ width: '16px', height: '19px' }}
                  icon={<ButtonArrow rotate={'180deg'} fill={'#00323D'} />}
                  width={95}
                  height={45}
                  onClick={() => { closeSteps(), setIsSecondStep(true) }}
                />
                <StoneityButton
                  buttonStyle='dark'
                  buttonText={t('continue')}
                  iconPosition='end'
                  iconStyle={{ width: '16px', height: '19px' }}
                  icon={<ButtonArrow />}
                  width={150}
                  height={45}
                  onClick={() => { closeSteps(), setIsFourthStep(true) }}
                />
              </div>
            </div>
          }
          {isFourthStep &&
            <div className={styles.userPageContent}>
              <Link href={"/"}>
                <Image
                  src={`${IMAGE_DELIVERY}/a7ef1d5a-a230-4954-ccc7-add056564300`}
                  alt=""
                  width={150}
                  height={37}
                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 150, 37)}
                />
              </Link>
              <div className={styles.userPageChoseWrapper}>
                <div className={styles.userPageChoseContent}>
                  <div className={styles.title}>{t('createInstanceFormTitle')}</div>
                  <TextField
                    isRequired
                    label='view'
                    labelText={t('stoneityAccountName')}
                    name='instanceName'
                    instanceInput={true}
                    loading={nameCheck === 'checking'}
                    success={nameCheck === 'ok'}
                    successText={t('accountNameAvailable')}
                    error={(inputErrors.instanceName && inputErrors.instanceName.length > 0) === true || nameCheck === 'taken'}
                    errorText={nameCheck === 'taken' ? t('accountNameUnavailable') : inputErrors.instanceName}
                    value={instanceData.instanceName}
                    onChange={handleChange}
                  />
                  <TextField
                    isRequired
                    label='view'
                    labelText={t('email')}
                    name='email'
                    error={(inputErrors.email && inputErrors.email.length > 0) === true}
                    errorText={inputErrors.email}
                    value={instanceData.email}
                    onChange={handleChange}
                  />
                  <TextField
                    isRequired
                    label='view'
                    labelText={t('firstName')}
                    name='firstName'
                    error={(inputErrors.firstName && inputErrors.firstName.length > 0) === true}
                    errorText={inputErrors.firstName}
                    value={instanceData.firstName}
                    onChange={handleChange}
                  />
                  <TextField
                    isRequired
                    label='view'
                    labelText={t('lastName')}
                    name='lastName'
                    error={(inputErrors.lastName && inputErrors.lastName.length > 0) === true}
                    errorText={inputErrors.lastName}
                    value={instanceData.lastName}
                    onChange={handleChange}
                  />
                  <TextField
                    isRequired={false}
                    label='view'
                    labelText={t('phone')}
                    name='phone'
                    error={(inputErrors.phone && inputErrors.phone.length > 0) === true}
                    errorText={inputErrors.phone}
                    value={instanceData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className={styles.buttonWrapper}>
                <StoneityButton
                  buttonStyle='light'
                  buttonText={t('back')}
                  iconPosition='start'
                  iconStyle={{ width: '16px', height: '19px' }}
                  icon={<ButtonArrow rotate={'180deg'} fill={'#00323D'} />}
                  width={95}
                  height={45}
                  onClick={() => { closeSteps(), setIsThirdStep(true) }}
                />
                <StoneityButton
                  buttonStyle='dark'
                  buttonText={t('createDemoInstance')}
                  iconPosition='start'
                  iconStyle={{ width: '16px', height: '19px' }}
                  width={200}
                  height={45}
                  disabled={!isFormReady || isLoading}
                  onClick={() => { if (isFormReady && !isLoading) createDemoInstance() }}
                />
              </div>
            </div>
          }
        </div>
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || 'en', ['common'])),
  },
});

export default TryDemo;